import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/domains/common/Common';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { SportMeetingsList } from 'src/domains/sportsbook/webview/components/containers/sportMeetingsList/SportMeetingsList';
import { QuickPick } from 'src/domains/sportsbook/webview/components/quickPick/QuickPick';
import {
    FiltersWrapper,
    SportsMeetingsWrapper,
} from 'src/domains/sportsbook/webview/components/racing/NewRacingPage.style';
import { FiltersNewView } from 'src/domains/layouts/webview/components/filters/Filters';
import { CompetitionsTrapChallenge } from 'src/domains/sportsbook/webview/components/containers/competitionsTrapChallenge/CompetitionsTrapChallenge';
import { FiltersScrollableWrapper } from 'src/domains/layouts/webview/components/filters/FiltersScrollableWrapper';
import { MeetingsSpecials } from 'src/domains/sportsbook/webview/components/meetings/specials/MeetingsSpecials';
import { MeetingsAntePost } from 'src/domains/sportsbook/webview/components/meetings/antePost/MeetingsAntePost';
import { SportRacesTypeParamType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';
import { getFilters } from './racingFilters';

interface PropsType {
    sport: 'horseracing' | 'greyhoundracing';
    filterType: SportRacesTypeParamType | null;
    eventId: EventId | null;
    isLoading: boolean;
}

export const RacingMeetingsContainer = observer('RacingMeetingsContainer', (props: PropsType) => {
    const { sport, eventId } = props;

    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const languagesState = LanguagesState.get(common);
    const breakpointsState = BreakpointsState.get(common);
    const { sportsbookVariant } = configState.componentVariants;
    const isTabletOrBigger = breakpointsState.tablet.isBiggerOrEq === true;
    const filterType = props.filterType ?? 'uk-ire';
    const state = AppSportsBookState.get(common).horseRacingState.get(sport);

    const selectedNew = state.selectedNew;

    const isAntePostReverse = filterType === 'ante-post' && configState.isAntePostReverse;

    const filters = getFilters(
        languagesState,
        state.showTrapChallenge,
        state.showSpecials,
        state.cleanSelected,
        isTabletOrBigger,
        sport
    );
    const { isLoading } = props;

    return (
        <SportsMeetingsWrapper
            data-test='sports-meetings-wrapper'
            isAntePostReverse={isAntePostReverse}
            variant={sportsbookVariant}
        >
            <FiltersWrapper>
                <FiltersScrollableWrapper>
                    <FiltersNewView
                        select={filterType}
                        filters={filters}
                    />
                </FiltersScrollableWrapper>
            </FiltersWrapper>
            <React.Fragment key='filters'>
                {filterType === 'uk-ire' || filterType === 'international' ? (
                    <SportMeetingsList
                        key={filterType}
                        sport={sport}
                        selected={selectedNew}
                        isLoading={isLoading}
                        isBuilding={false}
                        shared
                        filterType={filterType}
                    />
                ) : null}
                {filterType === 'quick-pick' ? (
                    <QuickPick
                        label={
                            <I18n
                                langKey='racing.quick-pick.label'
                                defaultText='quick pick'
                            />
                        }
                        key='quick-pick'
                        sport={sport}
                        isLoading={isLoading}
                        shared
                    />
                ) : null}
                {filterType === 'build-racecard' ? (
                    <SportMeetingsList
                        key='build-racecard'
                        sport={sport}
                        selected={selectedNew}
                        isBuilding
                        onChoose={state.toggleEvent}
                        isLoading={isLoading}
                        shared
                    />
                ) : null}
                {filterType === 'ante-post' ? (
                    <MeetingsAntePost
                        key='ante-post'
                        sport={sport}
                        filterType={filterType}
                        eventId={eventId}
                        isLoading={isLoading}
                        shared
                    />
                ) : null}
                {filterType === 'specials' ? (
                    <MeetingsSpecials
                        key='specials'
                        sport={sport}
                        filterType={filterType}
                        isLoading={isLoading}
                        eventId={eventId}
                        shared
                    />
                ) : null}
                {filterType === 'trap-challenge' ? (
                    <CompetitionsTrapChallenge
                        key='trap-challenge'
                        isLoading={isLoading}
                        sportEvents={state.trapChallengeEvents}
                        shared
                    />
                ) : null}
            </React.Fragment>
        </SportsMeetingsWrapper>
    );
});
